import LockOutlined from '@mui/icons-material/LockOutlined';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { Control, useWatch } from 'react-hook-form';

import DateInput from '~components/FormFields/v2/DateInput';
import Label from '~components/FormFields/v2/Label';
import TimeInput from '~components/FormFields/v2/TimeInput';
import { dateFormat } from '~utils/dateTime';

import { TicketDto } from './ticketSchema';

export type StampTimeFieldName =
  | 'arrivedPickupTime'
  | 'arrivedDropoffTime'
  | 'loadedTime'
  | 'unloadedTime'
  | 'loadCompletedTime';

export type StampDateFieldName =
  | 'arrivedPickupDate'
  | 'arrivedDropoffDate'
  | 'loadedDate'
  | 'unloadedDate'
  | 'loadCompletedDate';

interface StampDateTimeFieldProps {
  control: Control<TicketDto>;
  dateName: StampDateFieldName;
  isReadOnly?: boolean;
  label: string;
  timeName: StampTimeFieldName;
}

export default function StampDateTimeField({
  control,
  dateName,
  isReadOnly,
  label,
  timeName,
}: StampDateTimeFieldProps) {
  const theme = useTheme();
  const timeValue = useWatch({ control, name: timeName });
  const dateValue = useWatch({ control, name: dateName });

  const hasRecordedTime = Boolean(timeValue);
  // Read-only stamps (e.g. Load Completed) can't be edited, so their date is shown
  // as a caption rather than an editable picker.
  const showsDatePicker = hasRecordedTime && !isReadOnly;
  const showsDateCaption = hasRecordedTime && Boolean(dateValue) && !showsDatePicker;

  // Wrapped in a Box so the read-only Tooltip below has a ref-holding child.
  const timeInput = (
    <Box>
      <TimeInput
        control={control}
        isDisabled={isReadOnly}
        name={timeName}
        sx={isReadOnly ? { backgroundColor: theme.brandV2.colors.treadGray8 } : undefined}
      />
    </Box>
  );

  return (
    <Box>
      <Box sx={{ alignItems: 'center', display: 'flex', gap: 0.5, mb: 0.25 }}>
        <Label>{label}</Label>
        {isReadOnly && <LockOutlined sx={{ fontSize: '12px' }} />}
      </Box>

      {isReadOnly ? (
        <Tooltip title={t('loads.set_automatically_by_system')}>{timeInput}</Tooltip>
      ) : (
        timeInput
      )}

      {showsDateCaption && dateValue && (
        <Typography
          sx={{
            color: theme.brandV2.colors.treadGray5,
            fontSize: '10px',
            mt: 0.25,
          }}
        >
          {dateFormat(dateValue)}
        </Typography>
      )}

      {showsDatePicker && (
        <DateInput control={control} name={dateName} sx={{ mt: 0.5 }} />
      )}
    </Box>
  );
}
